import React from 'react'
import { Row, Col } from 'antd'
import { useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'

import FormattedMessage from 'components/FormattedMessage'
import Rating from 'components/Rating'
import Icon from 'components/Icon'
import FormattedPrice from 'components/FormattedPrice'
import { IntlMessageKeys } from 'components/FormattedMessage/types'

import PriceCalculationList from './PriceCalculation'
import TaxRatesList from './TaxRates'

import { lineItemsSummary } from 'helpers/offer'
import { combineDateAndTime } from 'helpers/date'

import { Provider } from 'generated/types'
import { useProviderPriceCalculationQuery } from './graphql.generated'

import classNames from 'helpers/css'

import styles from './index.module.scss'

const ProviderOverview: React.FC<ProviderOverviewProps> = (props) => {
  const {
    initialFormValues = {},
    provider: {
      id,
      pictures,
      displayTitle,
      location,
      address,
      type,
      rating,
    },
  } = props

  const timesStartsAt = useWatch({
    name: 'timesStartsAt',
    defaultValue: initialFormValues.timesStartsAt,
  })
  const timesEndsAt = useWatch({
    name: 'timesEndsAt',
    defaultValue: initialFormValues.timesEndsAt,
  })
  const numberOfPersons = useWatch({
    name: 'numberOfPersons',
    defaultValue: initialFormValues.numberOfPersons,
  })
  const requestedDates = useWatch({
    name: 'requestedDates',
    defaultValue: initialFormValues.requestedDates,
  })

  const intl = useIntl()

  const options = {
    variables: {
      id: id as string,
      startTime: combineDateAndTime(requestedDates, timesStartsAt as string).format(),
      endTime: combineDateAndTime(requestedDates, timesEndsAt as string).format(),
      numberOfGuests: numberOfPersons,
    },
    skip: !(numberOfPersons && timesStartsAt && timesEndsAt && requestedDates),
  }

  const { data } = useProviderPriceCalculationQuery(options)

  const priceCalculationItems = data?.provider?.priceCalculationItems || []
  const showAddress = type !== 'EventIdea' && address
  const itemsSummary = priceCalculationItems?.length
    ? lineItemsSummary({ items: priceCalculationItems, guestCount: null }, false, intl)
    : null

  return (
    <div className={classNames(styles, 'contact-request__overview')}>
      <div className={classNames(styles, 'contact-request__overview__content')}>
        {showAddress && (
          <address className={classNames(styles, 'contact-request__overview__content__address')}>
            <Icon name="map-marker" />
            {[address.street, address.streetNumber, address.postcode, location].join(' ')}
          </address>
        )}

        <Row gutter={8} className={classNames(styles, 'contact-request__overview__content__row')}>
          <Col span={10} className={classNames(styles, 'contact-request__overview__content__col')}>
            <img
              className={classNames(styles, 'contact-request__overview__content__picture')}
              src={pictures?.[0]?.url}
            />
          </Col>

          <Col span={14} className={classNames(styles, 'contact-request__overview__content__col')}>
            <div className={classNames(styles, 'contact-request__overview__content__title')}>
              {displayTitle}
            </div>

            {type && (
              <div className={classNames(styles,
                'contact-request__overview__content__col__content--secondary',
              )}>
                <FormattedMessage id={`providerType.${type}` as IntlMessageKeys} />
              </div>
            )}

            {rating && (
              <Rating
                rating={rating}
              />
            )}
          </Col>
        </Row>

        <PriceCalculationList priceCalculationItems={priceCalculationItems} />

        <TaxRatesList taxRates={itemsSummary?.taxRates || {}} />

        {itemsSummary?.gross && (
          <div className={classNames(styles, 'contact-request__overview__content__footer')}>
            <span>
              <FormattedMessage id="contactRequest.providerOverview.sum" />
              <span className={classNames(styles, 'contact-request__overview__content__footer__tax')}>
                <FormattedMessage id="contactRequest.providerOverview.tax" />
              </span>
            </span>
            <p>
              <FormattedPrice price={itemsSummary.gross} />
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

type ProviderOverviewProps = {
  initialFormValues: {
    requestedDates?: string | Array<string>,
    numberOfPersons?: number,
    timesStartsAt?: string,
    timesEndsAt?: string,
  },
  provider: Provider,
}

export default ProviderOverview
